import { SAFETY_LIMITS } from './limits.js';

/**
 * A quién atiende Cadencia, y qué hace con quien queda fuera (encargo del
 * 23/09/2026, §8).
 *
 * Las reglas de volumen, progresión y descarga de este dominio están escritas
 * para ADULTOS SANOS que entrenan fuerza, y la evidencia que las respalda
 * (p. ej. la posición ACSM de 2026) se refiere a esa población. Extrapolarlas a
 * un menor, a un embarazo o posparto, a una enfermedad relevante o a una
 * rehabilitación sería inventar una prescripción que nadie ha validado.
 *
 * Lo que se hace NO es bloquear por pertenecer a un grupo: es no generar un
 * plan automático sin más, decir por qué y a quién acudir.
 *
 *   - **Menor de edad** (por la fecha de nacimiento declarada): no se crea
 *     plan. Orientación limitada y derivación. La edad se conoce siempre que
 *     haya fecha, así que la regla se aplica en todos los caminos que crean un
 *     primer bloque (`crearPrimerBloque`), no solo en el test.
 *   - **Embarazo o posparto, enfermedad relevante, rehabilitación**: no se crea
 *     plan salvo que la persona confirme que un profesional sanitario le ha
 *     dado el visto bueno para entrenar fuerza. Con ese visto bueno el plan
 *     sigue siendo el general y se dice, porque adaptarlo es cosa de ese
 *     profesional.
 *
 * Estas situaciones NO SE GUARDAN. Son datos de salud (categoría especial del
 * RGPD, art. 9) y la aplicación no los necesita después del test: guardar
 * menos es la protección más barata. La consecuencia, que queda escrita: si
 * más tarde se crea el plan desde Entrenar, la aplicación no recuerda lo
 * contestado aquí; el aviso de la pantalla final lo dice.
 *
 * Esto no es un triaje clínico ni pretende serlo.
 */

export const POPULATION_SITUATIONS = [
  'pregnancy_postpartum',
  'medical_condition',
  'rehabilitation',
] as const;

export type PopulationSituation = (typeof POPULATION_SITUATIONS)[number];

export type OutOfScopeReason = 'minor' | PopulationSituation;

export const ETIQUETA_SITUACION: Record<PopulationSituation, string> = {
  pregnancy_postpartum: 'Estoy embarazada o he dado a luz en el último año',
  medical_condition:
    'Tengo una enfermedad que sigue un médico y que puede afectar al ejercicio (corazón, tensión, diabetes, respiratoria…)',
  rehabilitation: 'Estoy en rehabilitación de una lesión o de una operación',
};

export const MENSAJE_FUERA_DE_ALCANCE: Record<OutOfScopeReason, string> = {
  minor:
    'Cadencia está pensada para adultos. Con menos de 18 años no voy a crear un plan automático: busca un entrenador cualificado que te vea en persona y, si hace falta, a tu médico.',
  pregnancy_postpartum:
    'En el embarazo y el posparto el entrenamiento de fuerza se adapta con criterios que esta aplicación no aplica. Consúltalo con tu matrona, tu ginecóloga o tu médico antes de seguir un plan.',
  medical_condition:
    'Con una enfermedad que sigue un médico, conviene que sea él quien diga qué esfuerzo te conviene. Mis reglas están hechas para adultos sanos.',
  rehabilitation:
    'Mientras dure la rehabilitación, quien marca las cargas es tu fisioterapeuta o tu médico. Mis reglas no están hechas para readaptar una lesión.',
};

export interface PopulationInput {
  /** `YYYY-MM-DD` o `null` si no la ha dicho. */
  birthDate: string | null;
  /** Fecha local de hoy, `YYYY-MM-DD`. */
  today: string;
  situations: readonly PopulationSituation[];
  /** Si ha confirmado el visto bueno de un profesional sanitario. */
  professionalClearance: boolean;
}

export interface PopulationScope {
  /** Si se puede crear un plan automático. */
  planAllowed: boolean;
  /** Todo lo que queda fuera del alcance general, esté o no autorizado el plan. */
  reasons: OutOfScopeReason[];
}

/** Años cumplidos en `today`, contando en fechas locales. */
export function ageOn(birthDate: string, today: string): number {
  const [by, bm, bd] = birthDate.split('-').map(Number) as [number, number, number];
  const [ty, tm, td] = today.split('-').map(Number) as [number, number, number];
  const cumplido = tm > bm || (tm === bm && td >= bd);
  return ty - by - (cumplido ? 0 : 1);
}

export function isMinor(birthDate: string | null, today: string): boolean {
  return birthDate !== null && ageOn(birthDate, today) < SAFETY_LIMITS.population.minAdultAgeYears;
}

export function populationScope(input: PopulationInput): PopulationScope {
  const reasons: OutOfScopeReason[] = [];
  const menor = isMinor(input.birthDate, input.today);
  if (menor) reasons.push('minor');
  for (const s of POPULATION_SITUATIONS) if (input.situations.includes(s)) reasons.push(s);

  // Un menor no se desbloquea con un visto bueno declarado en un formulario.
  const planAllowed = !menor && (input.situations.length === 0 || input.professionalClearance);
  return { planAllowed, reasons };
}
